/**
 * InterestStatsPanel Component
 *
 * Displays aggregated investor interest statistics for a campaign.
 * Shows favorites count, soft-commitment count, and commitment totals.
 *
 * This component provides admins with visibility into investor sentiment
 * and non-binding interest before campaigns go live.
 *
 * @component
 * @example
 * <InterestStatsPanel
 *   campaignId={123}
 *   currency="USD"
 * />
 */

import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Skeleton,
  Alert,
  Tooltip,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Favorite as FavoriteIcon,
  HandshakeOutlined as CommitmentIcon,
  AttachMoney as MoneyIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { getInterestStats, recalculateStats } from "../../redux/actions/dealFlowActions";
import { fmt$ } from "../../utils/format";
import "./InterestStatsPanel.scss";

/**
 * Format a soft-commitment amount in the currency it was recorded in.
 *
 * This was a PRIVATE SHADOW of the shared helper, defaulting to "USD" and
 * formatting with en-US — directly contradicting utils/format.js, whose
 * DEFAULT_CURRENCY is "CAD" and whose formatter is en-CA. So the same amount
 * rendered differently depending on which admin screen you were on, and a
 * soft-commitment with no recorded currency was labelled USD here and CAD
 * everywhere else.
 *
 * The currency is now REQUIRED rather than defaulted: these figures come from
 * deal_flow soft-commitment rows that carry their own `currency` column
 * (inherited from the campaign), so there is always a real answer and guessing
 * one is never right. An absent currency renders the bare number instead of
 * asserting a wrong unit.
 *
 * @param {number} amount
 * @param {string} [currency] ISO code from the soft-commitment row
 */
const formatCurrency = (amount, currency) => {
  if (amount === null || amount === undefined) return "—";
  if (!currency) return Number(amount).toLocaleString("en-CA");
  return fmt$(amount, 0, currency);
};

/**
 * StatCard - Individual statistic display card
 */
const StatCard = ({ icon, label, value, subValue, tooltip, loading }) => (
  <Paper className="interest-stats__card" elevation={0}>
    {loading ? (
      <Box className="interest-stats__card-content">
        <Skeleton variant="circular" width={40} height={40} />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />
      </Box>
    ) : (
      <Box className="interest-stats__card-content">
        <Box className="interest-stats__icon-wrapper">{icon}</Box>
        <Typography variant="h4" className="interest-stats__value">
          {value}
        </Typography>
        <Typography variant="body2" className="interest-stats__label">
          {label}
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="top">
              <InfoIcon className="interest-stats__info-icon" />
            </Tooltip>
          )}
        </Typography>
        {subValue && (
          <Typography variant="caption" className="interest-stats__subvalue">
            {subValue}
          </Typography>
        )}
      </Box>
    )}
  </Paper>
);

const InterestStatsPanel = ({
  campaignId,
  currency = "USD",
  showRecalculate = true,
  compact = false,
}) => {
  const dispatch = useDispatch();

  // Select state from Redux store
  const { interestStats, statsLoading, recalculating, statsError } = useSelector(
    (state) => state.dealFlow
  );

  // Get stats for this specific campaign
  const stats = interestStats[campaignId] || null;

  // Fetch stats on mount
  useEffect(() => {
    if (campaignId) {
      dispatch(getInterestStats(campaignId));
    }
  }, [dispatch, campaignId]);

  // Handle manual refresh/recalculate
  const handleRecalculate = () => {
    if (campaignId && !recalculating) {
      dispatch(recalculateStats(campaignId));
    }
  };

  const commitmentTotalMin =
    stats?.soft_commitment_total_amount ??
    stats?.soft_commitment_total_min ??
    0;
  const commitmentTotalMax =
    stats?.soft_commitment_total_amount ??
    stats?.soft_commitment_total_max ??
    0;

  // Loading state
  const isLoading = statsLoading && !stats;

  // Error state
  if (statsError && !stats) {
    return (
      <Alert severity="error" className="interest-stats__error">
        Failed to load interest statistics. Please try again.
      </Alert>
    );
  }

  // Compact mode for list views
  if (compact) {
    return (
      <Box className="interest-stats interest-stats--compact">
        <Box className="interest-stats__compact-row">
          <Tooltip title="Favorites" arrow>
            <Box className="interest-stats__compact-item">
              <FavoriteIcon fontSize="small" color="error" />
              <Typography variant="body2">
                {isLoading ? "—" : stats?.favorite_count ?? 0}
              </Typography>
            </Box>
          </Tooltip>
          <Tooltip title="Soft Commitments" arrow>
            <Box className="interest-stats__compact-item">
              <CommitmentIcon fontSize="small" color="primary" />
              <Typography variant="body2">
                {isLoading ? "—" : stats?.soft_commitment_count ?? 0}
              </Typography>
            </Box>
          </Tooltip>
          {commitmentTotalMax > 0 && (
            <Tooltip title="Commitment Total" arrow>
              <Box className="interest-stats__compact-item">
                <MoneyIcon fontSize="small" color="success" />
                <Typography variant="body2">
                  {formatCurrency(commitmentTotalMin, currency)}
                  {commitmentTotalMax > commitmentTotalMin && (
                    <> - {formatCurrency(commitmentTotalMax, currency)}</>
                  )}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box className="interest-stats">
      <Box className="interest-stats__header">
        <Typography variant="h6" className="interest-stats__title">
          Investor Interest
        </Typography>
        {showRecalculate && (
          <Tooltip title="Recalculate statistics" arrow>
            <IconButton
              size="small"
              onClick={handleRecalculate}
              disabled={recalculating || isLoading}
              className="interest-stats__refresh-btn"
            >
              <RefreshIcon
                className={recalculating ? "interest-stats__refresh-spinning" : ""}
              />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Grid container spacing={2} className="interest-stats__grid">
        {/* Favorites Count */}
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<FavoriteIcon className="interest-stats__icon interest-stats__icon--favorites" />}
            label="Favorites"
            value={isLoading ? "—" : stats?.favorite_count ?? 0}
            tooltip="Number of investors who have favorited this campaign"
            loading={isLoading}
          />
        </Grid>

        {/* Soft Commitments Count */}
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<CommitmentIcon className="interest-stats__icon interest-stats__icon--commitments" />}
            label="Soft Commitments"
            value={isLoading ? "—" : stats?.soft_commitment_count ?? 0}
            tooltip="Non-binding expressions of investment interest"
            loading={isLoading}
          />
        </Grid>

        {/* Commitment Amount Range */}
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<MoneyIcon className="interest-stats__icon interest-stats__icon--amount" />}
            label="Commitment Total"
            value={
              isLoading
                ? "—"
                : commitmentTotalMax > 0
                ? formatCurrency(commitmentTotalMin, currency)
                : "No commitments"
            }
            subValue={
              !isLoading && commitmentTotalMax > commitmentTotalMin
                ? `to ${formatCurrency(commitmentTotalMax, currency)}`
                : null
            }
            tooltip="Total soft-commitment amount"
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Additional context for Coming Soon campaigns */}
      {stats?.soft_commitment_count > 0 && (
        <>
          <Divider className="interest-stats__divider" />
          <Typography variant="body2" className="interest-stats__note">
            Soft commitments are non-binding expressions of interest. Investors will be
            notified when this campaign goes live.
          </Typography>
        </>
      )}
    </Box>
  );
};

export default InterestStatsPanel;
